import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Coins, Pause, Play, ShieldCheck, ShieldAlert } from 'lucide-react';
import { StablecoinForms } from './StablecoinForms';
import { StablecoinAssets, StablecoinRedemptions } from './StablecoinLists';
import { PoolFundCard } from './PoolFundCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-treasury-check/admin/stablecoin`;
const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat = ({ k, v, testid, tone = 'text-white' }) => (
  <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-700">
    <p className="text-xs text-slate-400">{k}</p>
    <p className={`text-lg font-bold ${tone}`} data-testid={testid}>{v}</p>
  </div>
);

export const StablecoinPanel = ({ network, token, onChanged }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(() => {
    axios.get(`${API}/status`, { params: { network }, headers: { Authorization: `Bearer ${token}` } })
      .then(r => setD(r.data)).catch(() => setD(null));
  }, [network, token]);
  useEffect(() => { load(); }, [load]);

  const post = async (path, body = {}, done) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/${path}`, { network, ...body }, auth);
      toast.success(r.data.message || 'Done');
      done && done();
      load(); onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transaction failed');
    }
    setBusy(false);
  };

  if (!d?.enabled || !d?.stablecoin) return null;
  const s = d.stablecoin;
  return (
    <div className="bg-slate-800 border border-emerald-500/30 rounded-xl p-5 space-y-5" data-testid="stablecoin-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Coins className="w-5 h-5 text-emerald-400" /> {s.name} ({s.symbol}) Stablecoin</h3>
          <p className="text-xs text-slate-400">1 BETH = $1.00 · minting only while recorded cash + Treasury reserves cover supply dollar-for-dollar</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${s.fully_backed ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`} data-testid="stablecoin-backed-badge">
            {s.fully_backed ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />} {s.fully_backed ? 'Fully backed' : 'Under-backed'}
          </span>
          <button onClick={() => post(s.paused ? 'unpause' : 'pause')} disabled={busy} data-testid="stablecoin-pause-btn"
            className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 disabled:opacity-40 ${s.paused ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-slate-900'}`}>
            {s.paused ? <><Play className="w-4 h-4" /> Unpause</> : <><Pause className="w-4 h-4" /> Pause transfers</>}
          </button>
        </div>
      </div>
      {s.paused && <p className="text-sm text-amber-300" data-testid="stablecoin-paused-note">Transfers, minting and redemptions are paused.</p>}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat k="Outstanding BETH" v={usd(s.outstanding_usd)} testid="stablecoin-outstanding" />
        <Stat k="Cash reserve" v={usd(s.cash_reserve_usd)} testid="stablecoin-cash" />
        <Stat k="Treasury reserve" v={usd(s.treasury_reserve_usd)} testid="stablecoin-treasury" />
        <Stat k="Backing" v={s.backing_pct == null ? 'n/a' : `${s.backing_pct.toLocaleString()}%`} testid="stablecoin-backing" tone={s.fully_backed ? 'text-emerald-400' : 'text-red-400'} />
        <Stat k="Can still mint" v={usd(s.mint_capacity_usd)} testid="stablecoin-capacity" />
      </div>
      {d.pending_migration > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3" data-testid="stablecoin-migration">
          <p className="text-sm text-amber-200">{d.pending_migration} balance(s) from the previous check contract are waiting. Record enough reserves, then carry them over.</p>
          <button onClick={() => post('migrate')} disabled={busy} data-testid="stablecoin-migrate-btn" className="px-3 py-1.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-semibold disabled:opacity-40">Carry over balances</button>
        </div>
      )}
      <StablecoinForms busy={busy} post={post} />
      {network === 'base' && <PoolFundCard token={token} onChanged={load} />}
      <StablecoinAssets assets={s.assets} busy={busy} post={post} />
      <StablecoinRedemptions redemptions={s.redemptions} busy={busy} post={post} />
    </div>
  );
};
